/**
 * Phase 27.53 — Reusable 16:9 image cropper.
 *
 * Opens a modal with the raw picked image plus zoom + drag controls,
 * then emits a cropped `File` via `onCropped` so every consumer stores
 * a consistent 16:9 asset. Keeps upload consumers unchanged — they
 * still receive a `File` on save.
 */
import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const OUTPUT_MAX_WIDTH = 1600; // 1600×900 keeps the export sharp.

// Draw the selected crop rectangle onto a canvas and export as JPEG.
async function cropToFile(srcUrl, cropArea, sourceFileName) {
  const img = await new Promise((res, rej) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => res(el);
    el.onerror = rej;
    el.src = srcUrl;
  });
  const scale = Math.min(1, OUTPUT_MAX_WIDTH / cropArea.width);
  const outW = Math.round(cropArea.width * scale);
  const outH = Math.round(outW * 9 / 16);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    cropArea.x, cropArea.y, cropArea.width, cropArea.height,
    0, 0, outW, outH,
  );
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Could not export cropped image');
  const cleanName = (sourceFileName || 'cover').replace(/\.[a-z0-9]+$/i, '');
  return new File([blob], `${cleanName}-16x9.jpg`, { type: 'image/jpeg' });
}

export default function ImageCropper16x9({
  file,
  onCropped,
  onCancel,
  title = 'Adjust cover (16:9)',
}) {
  const [imgUrl, setImgUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const handleSave = async () => {
    if (!pixels || !imgUrl) return;
    setSaving(true);
    try {
      const cropped = await cropToFile(imgUrl, pixels, file?.name);
      onCropped?.(cropped);
    } catch (err) {
      toast.error(err?.message || 'Could not crop image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-2xl" data-testid="cover-cropper-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative w-full h-[360px] bg-slate-900 rounded-lg overflow-hidden">
            {imgUrl && (
              <Cropper
                image={imgUrl}
                crop={crop}
                zoom={zoom}
                aspect={16 / 9}
                showGrid
                restrictPosition
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Zoom</Label>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
              data-testid="cover-cropper-zoom"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Drag to reposition · pinch or use the slider to zoom · output is 16:9 (up to 1600×900).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            data-testid="cover-cropper-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !pixels}
            data-testid="cover-cropper-save"
          >
            {saving ? 'Cropping…' : 'Use this crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
